import { imageUuid } from 'editor/src/store/gallery/reducer/addImageReducer';
import getImageById from 'editor/src/store/gallery/selector/getImageById';
import { GalleryImage } from 'editor/src/store/gallery/types';
import toGalleryImages from 'editor/src/store/gallery/utils/toGalleryImages';
import type { ThunkAction } from 'editor/src/store/hooks';

import { getLibraryRequest } from '../selector';
import {
  clearLibraryRequestAction,
  libraryImageRemovedAction,
  libraryImageSavedAction,
  setLibraryRequestAction,
} from '../slice';
import { LibraryRequest, LibraryUpdateResult } from '../types';
import isUploadedImageData from '../utils/isUploadedImageData';

async function getSavedImage(result: LibraryUpdateResult & { success: true }, galleryImage: GalleryImage | undefined) {
  if (isUploadedImageData(result.image)) {
    const [image] = await toGalleryImages([result.image]);
    return { ...image, uuid: imageUuid.generate() };
  }
  if (!galleryImage) {
    return undefined;
  }
  // the library lists every saved image, also those the gallery hides
  const { hidden, ...image } = galleryImage;
  return { ...image, uuid: imageUuid.generate() };
}

// retrying cannot help: the image is gone, or the customer is no longer signed in
const FINAL_ERRORS = ['not_found', 'unauthorized'];

// a request the editor stopped waiting for still takes the host's late answer: it tells the real outcome
function answersRequest(request: LibraryRequest | undefined, saved: boolean) {
  return request?.saved === saved && (request.status === 'pending' || !!request.timedOut);
}

/**
 * Handles `gallery.libraryUpdateResult` and resolves to whether it was applied. Results carry no
 * request id, so one that answers no request of its kind (a late save while a remove is pending, a
 * duplicate) is dropped. Only a confirmed result changes what the library shows.
 */
const applyLibraryUpdateResultOperation =
  (result: LibraryUpdateResult): ThunkAction<Promise<boolean>> =>
  async (dispatch, getState) => {
    const { imageId, saved } = result;
    if (!answersRequest(getLibraryRequest(getState(), imageId), saved)) {
      return false;
    }

    if (!result.success) {
      dispatch(
        result.error && FINAL_ERRORS.includes(result.error)
          ? clearLibraryRequestAction(imageId)
          : setLibraryRequestAction({ imageId, request: { status: 'failed', saved } }),
      );
      return true;
    }

    if (!saved) {
      dispatch(libraryImageRemovedAction(imageId));
      return true;
    }

    try {
      const image = await getSavedImage(result, getImageById(getState(), imageId));
      dispatch(image ? libraryImageSavedAction(image) : clearLibraryRequestAction(imageId));
    } catch {
      dispatch(clearLibraryRequestAction(imageId));
    }
    return true;
  };

export default applyLibraryUpdateResultOperation;
